import { useMemo, useState } from "preact/hooks";
import type { Stack, StackOsInfo } from "../../types";
import { webClientUrl } from "../../config";
import { ContentCell, FeaturesDisplay } from "./ContentCell";
import { rowClass, useSortableRows } from "../../utils/useSortableRows";

interface Column {
  key: keyof Stack | "content";
  label: string;
}

const COLUMNS: Column[] = [
  { key: "id", label: "Id" },
  { key: "solution", label: "Solution" },
  { key: "region", label: "Region (AZ)" },
  { key: "platform", label: "Platform" },
  { key: "version", label: "Version" },
  { key: "schedule", label: "Schedule" },
  { key: "linux", label: "Linux" },
  { key: "windows", label: "Windows" },
  { key: "environment", label: "Environment" },
  { key: "content", label: "Content" },
  { key: "status", label: "Status" },
  { key: "playerId", label: "PlayerId" },
  { key: "clientIp", label: "ClientIp" },
  { key: "kioskId", label: "KioskId" },
];

const STATE_COLORS: Record<string, string> = {
  free: "#b0ffb9",
  running: "#b0ffb9",
  pending: "#feffb0",
  busy: "#ffb3b0",
  stopped: "#ffb3b0",
  stopping: "#ffd0b0",
  "shutting-down": "grey",
  terminated: "grey",
};

function ec2ConsoleUrl(region: string, stackId: string): string {
  const uriRegion = encodeURIComponent(region);
  return (
    `https://${uriRegion}.console.aws.amazon.com/ec2/home?region=${uriRegion}` +
    `#Instances:tag:dh:stack:id=${encodeURIComponent(stackId)};v=3;` +
    `$case=tags:true%5C,client:false;$regex=tags:false%5C,client:false`
  );
}

function sortValue(stack: Stack, key: Column["key"]): string {
  const raw = (stack as unknown as Record<string, unknown>)[key];
  if (raw && typeof raw === "object" && "instanceState" in raw) {
    return (raw as StackOsInfo).instanceState;
  }
  return String(raw ?? "").toLowerCase();
}

function OsCell({ info }: { info?: StackOsInfo }) {
  if (!info) return <td />;
  return (
    <td style={{ backgroundColor: STATE_COLORS[info.instanceState] }} title={info.instanceId}>
      {info.instanceState}
      {info.lifecycleType === "spot" && " (spot)"}
    </td>
  );
}

export function InstancesTable({
  stacks,
  selectedStackId,
  onSelectVersion,
}: {
  stacks: Record<string, Stack>;
  selectedStackId: string | null;
  onSelectVersion: (stack: Stack) => void;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const instanceList = useMemo(() => Object.values(stacks), [stacks]);
  const { sorted: rows, handleSort } = useSortableRows(instanceList, sortValue, "id" as Column["key"]);

  return (
    <table id="demandTable">
      <thead>
        <tr>
          {COLUMNS.map((col) => (
            <th key={col.key} onClick={() => handleSort(col.key)}>
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((stack, index) => {
          const isFree = ["free", ""].includes(stack.status);
          const isExpanded = expandedIds.has(stack.id);
          return (
            <>
              <tr
                key={stack.id}
                class={rowClass(stack.id === selectedStackId, index)}
                onClick={(e) =>
                  setExpandedIds((prev) => {
                    if (e.ctrlKey || e.metaKey) {
                      const next = new Set(prev);
                      next.has(stack.id) ? next.delete(stack.id) : next.add(stack.id);
                      return next;
                    }
                    return isExpanded ? new Set() : new Set([stack.id]);
                  })
                }
              >
                <td>
                  <a
                    href={ec2ConsoleUrl(stack.region, stack.id)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {stack.id}
                  </a>
                </td>
                <td>{stack.solution}</td>
                <td>
                  {stack.region} ({stack.availabilityZone.replace(stack.region, "")})
                </td>
                <td>{stack.platform}</td>
                <td
                  style={{ cursor: stack.platform === "ue5" ? "pointer" : "not-allowed" }}
                  title={stack.versionOriginal}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (stack.platform === "ue5") onSelectVersion(stack);
                  }}
                >
                  {stack.version}
                </td>
                <td class="schedule-cell">
                  {stack.scheduleRelated.length > 0 ? "▾" : ""}
                </td>
                <OsCell info={stack.linux} />
                <OsCell info={stack.windows} />
                <td>{stack.environment}</td>
                <td>
                  <ContentCell environment={stack.environment} solution={stack.solution} />
                </td>
                <td style={{ backgroundColor: STATE_COLORS[stack.status] }} onClick={(e) => e.stopPropagation()}>
                  <button
                    class="status-btn"
                    disabled={!isFree}
                    onClick={() => window.open(webClientUrl(stack.environment, stack.solution), "_blank")}
                  >
                    {stack.status || "open"}
                  </button>
                </td>
                <td>{stack.playerId}</td>
                <td>{stack.clientIp}</td>
                <td>{stack.kioskId}</td>
              </tr>
              {isExpanded && (
                <tr class="demand-details">
                  <td colSpan={5} />
                  <td>
                    {stack.scheduleRelated.length === 0 ? (
                      <i>No schedule info</i>
                    ) : (
                      stack.scheduleRelated.map(([key, value]) => (
                        <div key={key}>
                          <strong>{key}:</strong> {value}
                        </div>
                      ))
                    )}
                  </td>
                  <td>{stack.linux && <span><strong>Instance ID:</strong> {stack.linux.instanceId}</span>}</td>
                  <td>{stack.windows && <span><strong>Instance ID:</strong> {stack.windows.instanceId}</span>}</td>
                  <td />
                  <td><FeaturesDisplay environment={stack.environment} solution={stack.solution} /></td>
                  <td colSpan={4} />
                </tr>
              )}
            </>
          );
        })}
      </tbody>
    </table>
  );
}
